import { List, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { FormattedMessage, useIntl } from "react-intl";

import { AIR_MESSAGES } from "../message-definitions";

import { formatAirPeriod } from "./format-air-period";

dayjs.extend(LocalizedFormat);
dayjs.extend(utc);

export const BankStatementAirRequestMessage = ({
  bankData,
}: {
  bankData: FundingApplicationSchemas.BankStatementsForAccountAIRSchema;
}) => {
  const intl = useIntl();

  const bankName = bankData.bank_name
    ? bankData.bank_name
    : intl.formatMessage(AIR_MESSAGES.bankStatementDefaultBankName);
  const formatPeriod = (period: FundingApplicationSchemas.DateTimeRangeSchema) => formatAirPeriod(intl, period);
  const paragraph = (chunks: React.ReactNode) => <Text>{chunks}</Text>;

  if (bankData.periods.length > 1) {
    const values = {
      p: paragraph,
      bankName,
      bankAccountNum: bankData.bank_account_mask,
      periods: bankData.periods.map((period) => (
        <List.Item key={`${period.start}-${period.end}`}>
          <strong>{formatPeriod(period)}</strong>
        </List.Item>
      )),
    };

    return bankData.bank_account_mask ? (
      <FormattedMessage
        id="funding-application: air bank statement required account and periods description"
        defaultMessage="<p>Please upload statements for {bankName} account ending in {bankAccountNum} for the periods: {periods}</p>"
        values={values}
      />
    ) : (
      <FormattedMessage
        id="funding-application: air bank statement required account and periods description no mask"
        defaultMessage="<p>Please upload statements for your {bankName} account(s) for the periods: {periods}</p>"
        values={values}
      />
    );
  }

  const values = {
    p: paragraph,
    bankName,
    bankAccountNum: bankData.bank_account_mask,
    period: bankData.periods.map((period) => (
      <strong key={`${period.start}-${period.end}`}>{formatPeriod(period)}</strong>
    )),
  };

  return bankData.bank_account_mask ? (
    <FormattedMessage
      id="funding-application: air bank statement required account and period description"
      defaultMessage="<p>Please upload statements for {bankName} account ending in {bankAccountNum} for the period {period}.</p>"
      values={values}
    />
  ) : (
    <FormattedMessage
      id="funding-application: air bank statement required account and period description no mask"
      defaultMessage="<p>Please upload statements for your {bankName} account(s) for the period {period}.</p>"
      values={values}
    />
  );
};
